import { useState } from "react";
import { Field, Loading, useAction } from "../components/ui.tsx";
import { errorText, post, put, useApi } from "../lib/api.ts";
import type { AuthState, SystemStatus } from "../lib/types.ts";
import { BotStatusCard, DiscordCredentials, ServerPicker, useBot } from "./settings/Discord.tsx";
import { ClaudeLoginCard, useClaudeCheck } from "./settings/Claude.tsx";
import { ProjectsRootField } from "./settings/Server.tsx";

const STEPS = ["Password", "Discord bot", "Claude login", "Projects root"];

/** First-run wizard (spec §3): password → Discord bot → Claude login → projects root. */
export function Setup({ auth, onAuthChange }: { auth: AuthState; onAuthChange: () => void }) {
  const [step, setStep] = useState(auth.passwordSet ? 1 : 0);
  const current = !auth.passwordSet ? 0 : Math.max(step, 1);

  return (
    <div className="center">
      <div className="wizard">
        <div className="row">
          <img src="/favicon.svg" alt="" width={28} height={28} />
          <h1>Set up Switchboard</h1>
        </div>
        <div className="steps" aria-label={`Step ${current + 1} of ${STEPS.length}`}>
          {STEPS.map((s, i) => (
            <span key={s} className={i <= current ? "on" : ""} title={s} />
          ))}
        </div>
        <div className="muted small">
          Step {current + 1} of {STEPS.length} · {STEPS[current]}
        </div>
        {current === 0 && <PasswordStep onDone={onAuthChange} />}
        {current === 1 && <DiscordStep onNext={() => setStep(2)} />}
        {current === 2 && <ClaudeStep onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        {current === 3 && <RootStep onBack={() => setStep(2)} onFinish={onAuthChange} />}
      </div>
    </div>
  );
}

function PasswordStep({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await post("/api/auth/setup", { password });
          onDone();
        } catch (err) {
          setError(errorText(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Choose an admin password</h2>
      <p className="muted">This dashboard is single-user. Until a password is set, anyone who can reach the server can configure it, so do this first.</p>
      <Field label="Password" hint="At least 8 characters.">
        <input type="password" autoFocus autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Field label="Password again" error={again && again !== password ? "Does not match" : error ?? undefined}>
        <input type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
      </Field>
      <div className="row between">
        <span />
        <button className="btn primary" type="submit" disabled={busy || password.length < 8 || password !== again}>
          {busy ? "Saving…" : "Continue"}
        </button>
      </div>
    </form>
  );
}

function DiscordStep({ onNext }: { onNext: () => void }) {
  const bot = useBot(true);
  return (
    <div className="card">
      <h2>Connect the Discord bot</h2>
      <ol className="muted small" style={{ margin: 0 }}>
        <li>
          In the{" "}
          <a href="https://discord.com/developers/applications" target="_blank" rel="noreferrer">
            Discord developer portal
          </a>
          , create an application and add a bot.
        </li>
        <li>Copy the bot token and the application ID below, and turn on Message Content Intent.</li>
        <li>Open the invite link to add the bot to your server, then pick the server.</li>
      </ol>
      {bot.data && bot.data.state !== "disabled" && <BotStatusCard bot={bot.data} />}
      <DiscordCredentials bot={bot.data} onSaved={() => void bot.mutate()} />
      {bot.data && bot.data.state !== "disabled" && <ServerPicker bot={bot.data} />}
      <div className="row between">
        <span className="muted small">You can do this later in Settings → Discord.</span>
        <div className="row">
          <button className="btn" onClick={onNext}>
            Skip for now
          </button>
          <button className="btn primary" disabled={bot.data?.state !== "ready" || !bot.data.guild} onClick={onNext}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function ClaudeStep({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { system, recheck, checking } = useClaudeCheck();
  return (
    <div className="card">
      <h2>Check the Claude login</h2>
      <p className="muted">Sessions run under the Claude Code login on this server (your Max plan), never an API key.</p>
      {system.data ? <ClaudeLoginCard claude={system.data.claude} onRecheck={() => recheck()} checking={checking} /> : <Loading />}
      <div className="row between">
        <button className="btn ghost" onClick={onBack}>
          Back
        </button>
        <button className={`btn ${system.data?.claude.loggedIn ? "primary" : ""}`} onClick={onNext}>
          {system.data?.claude.loggedIn ? "Continue" : "Continue anyway"}
        </button>
      </div>
    </div>
  );
}

function RootStep({ onBack, onFinish }: { onBack: () => void; onFinish: () => void }) {
  const system = useApi<SystemStatus>("/api/system");
  const [finish, finishing] = useAction(async () => {
    await put("/api/settings", { "setup.completed": true });
    onFinish();
  });
  const ok = system.data?.projectsRoot.exists;
  return (
    <div className="card">
      <h2>Where do projects live?</h2>
      <p className="muted">Pick the folder that holds (or will hold) your repositories. Sessions get git worktrees inside each project, and nothing outside this folder can be used.</p>
      <ProjectsRootField onSaved={() => void system.mutate()} />
      <div className="row between">
        <button className="btn ghost" onClick={onBack}>
          Back
        </button>
        <button className="btn primary" disabled={!ok || finishing} onClick={() => finish()}>
          Finish setup
        </button>
      </div>
    </div>
  );
}
